<template>
  <div class="wxjs-pay-root">
    <div class="page">
      <div class="brand">微信支付</div>
      <div class="sub">{{ brandSub }}</div>

      <div class="card">
        <div class="amount-row">
          <span class="currency">¥</span>
          <span class="amount">{{ amountDisplay }}</span>
        </div>

        <div v-if="countdownVisible" class="countdown-row">
          订单剩余 <span class="countdown-val">{{ countdownText }}</span>
        </div>

        <p v-if="statusHint" class="hint" :class="{ err: isErrHint }">{{ statusHint }}</p>

        <!-- 扫码：保存图片 / 打开微信。微信 JS 在微信外同样出码 -->
        <div v-show="showQr" class="qr-wrap">
          <div class="qr-shell">
            <div ref="qrHost"></div>
          </div>
          <p class="qr-guide">请使用微信「扫一扫」</p>
          <div class="qr-actions">
            <button type="button" class="btn-save" @click="onSaveQr">保存图片</button>
            <button type="button" class="btn-open-wx" @click="onOpenWeixin">
              <svg class="wx-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path fill="currentColor" d="M9.02 3.2C4.72 3.2 1.25 6.05 1.25 9.58c0 1.96 1.05 3.72 2.72 4.9a.35.35 0 0 1 .12.4l-.3 1.08c-.09.32.24.6.55.44l1.42-.7a.36.36 0 0 1 .3-.02c.85.27 1.78.42 2.76.42.15 0 .3 0 .45-.02A5.5 5.5 0 0 1 9 14.2c0-3.4 3.2-6.15 7.15-6.15.25 0 .5.01.74.03C15.9 5.05 12.7 3.2 9.02 3.2zm-2.1 4.75a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8zm4.4 0a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8zM16.15 9.2c-3.2 0-5.8 2.2-5.8 4.9s2.6 4.9 5.8 4.9c.66 0 1.3-.1 1.88-.29a.3.3 0 0 1 .23.01l1.06.53c.23.11.48-.09.41-.33l-.23-.82a.3.3 0 0 1 .04-.3c1.24-.93 2.04-2.3 2.04-3.87 0-2.7-2.6-4.9-5.43-4.9zm-2.15 3.3a.68.68 0 1 1 0-1.36.68.68 0 0 1 0 1.36zm4.3 0a.68.68 0 1 1 0-1.36.68.68 0 0 1 0 1.36z"/>
              </svg>
              <span>打开微信</span>
            </button>
          </div>
          <p class="wx-scan-tip">可先保存图片，再打开微信用「扫一扫 → 相册」识别</p>
          <div v-show="showCopyGuide" class="copy-guide">
            <p class="copy-title">截图扫码失败时</p>
            <p class="copy-steps">请复制支付链接，打开微信发到聊天<br>然后点开链接完成支付</p>
            <div class="qr-link-row">
              <span class="qr-link-label">支付链接：</span>
              <span ref="payLinkText" class="qr-link-text" :title="resolveScanPayUrl()">{{ resolveScanPayUrl() }}</span>
              <button type="button" class="btn-copy-link" @click="onCopyPayLink">{{ copyBtnText }}</button>
            </div>
          </div>
          <div v-if="showQrLink && !showCopyGuide" class="qr-link-row">
            <span class="qr-link-label">二维码链接：</span>
            <span ref="qrLinkText" class="qr-link-text" :title="resolveScanPayUrl()">{{ resolveScanPayUrl() }}</span>
            <button type="button" class="btn-copy-link" @click="onCopyQrLink">{{ copyBtnText }}</button>
          </div>
        </div>
        <div v-if="saveOverlaySrc" class="save-mask" @click.self="closeSaveOverlay">
          <div class="save-panel">
            <img class="save-img" :src="saveOverlaySrc" alt="支付码"/>
            <p class="save-hint">{{ saveHint }}</p>
            <button type="button" class="btn-close-save" @click="closeSaveOverlay">关闭</button>
          </div>
        </div>

        <button
          v-if="jsPayMode === 'mp' && mpLaunchUrl && !paid && !expired"
          type="button"
          class="btn-primary"
          @click="openMiniProgram"
        >进入小程序支付</button>
        <p v-if="jsPayMode === 'mp' && mpLaunchUrl && !paid && !expired" class="wx-scan-tip">
          打开小程序后，再点页面上的「立即支付」拉起微信支付
        </p>

        <div class="meta">
          <div class="meta-row"><span>商户订单号</span><span class="meta-val">{{ mchOrderNo || '—' }}</span></div>
          <div class="meta-row"><span>下单时间</span><span class="meta-val">{{ createTime || '—' }}</span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchWxJsOrderBrief, queryWxJsOrder } from '../api/api'
import { renderQrInto, captureQrDataUrl } from '../utils/qrRender'
import { openWeixinApp } from '../utils/openWeixinScan'
import { copyTextReliable, isIosLike, saveHintText, saveQrFromDataUrl, selectElementText } from '../utils/wxjsQrActions'

/** 扫码壳样式用 JS 注入，避免 postcss-px2rem 把 scoped px 缩成看不清 */
const STYLE_ID = 'wxjs-pay-stylesheet'

const PAID_STATUSES = new Set([2010005, 2010006, 2010007, 2010008, 2010009, 2010012, 2010013])
const STATE_CLOSED = 2010002
const STATE_WRONG_AMOUNT = 2010011

function isPaidStatus (status) {
  return PAID_STATUSES.has(Number(status))
}

const QUERY_INTERVAL_MS = 2000
const QUERY_MAX_TIMES = 30

function qs (name) {
  const hash = window.location.hash || ''
  const qIndex = hash.indexOf('?')
  const search = qIndex >= 0 ? hash.substring(qIndex) : window.location.search
  const m = search.match(new RegExp('[?&]' + name + '=([^&]*)'))
  return m ? decodeURIComponent(m[1]) : ''
}

function isClientUa () {
  return /micromessenger/i.test(navigator.userAgent || '')
}

function errMsg (e, fallback) {
  if (!e) return fallback
  if (typeof e === 'string') return e
  return e.message || e.msg || e.errMsg || fallback
}

function formatMmSs (remainSec) {
  const s = Math.max(0, Math.floor(remainSec))
  const m = Math.floor(s / 60)
  const r = s % 60
  return String(m).padStart(2, '0') + ':' + String(r).padStart(2, '0')
}

export default {
  name: 'WxJsPay',
  data () {
    return {
      orderId: this.$route.params.orderId,
      token: qs('t') || (this.$route.query && this.$route.query.t) || '',
      amountFen: 0,
      amountYuan: '',
      mchOrderNo: '',
      createTime: '',
      error: '',
      inClient: isClientUa(),
      oauthStartUrl: '',
      oauthAuthorizeUrl: '',
      cashierUrl: '',
      paid: false,
      expired: false,
      expireAtMillis: null,
      nowMs: Date.now(),
      showScanShell: false,
      showCopyGuide: false,
      queryTimer: null,
      tickTimer: null,
      queryTimes: 0,
      lastRenderedQr: '',
      jsPayMode: 'oa',
      nativeScan: false,
      showQrLink: false,
      mpLaunchUrl: '',
      saveOverlaySrc: '',
      saveHint: '',
      saveObjectUrl: '',
      copyBtnText: '复制',
      copyResetTimer: null
    }
  },
  computed: {
    amountDisplay () {
      if (this.amountYuan) return this.amountYuan
      if (!this.amountFen) return '--'
      return (this.amountFen / 100).toFixed(2)
    },
    remainSec () {
      if (!this.expireAtMillis) return 0
      return Math.max(0, Math.floor((this.expireAtMillis - this.nowMs) / 1000))
    },
    countdownVisible () {
      return !this.paid && !this.expired && this.expireAtMillis != null && this.remainSec > 0
    },
    countdownText () {
      return formatMmSs(this.remainSec)
    },
    showQr () {
      return this.showScanShell && !this.paid && !this.expired && !!this.resolveScanPayUrl()
    },
    brandSub () {
      if (this.paid) return '支付已完成'
      if (this.expired) return '订单已过期'
      if (this.jsPayMode === 'mp') return '进入小程序完成支付'
      if (this.nativeScan || this.showScanShell) return '仅支持扫码，请使用微信扫一扫'
      if (this.inClient) return '正在进入支付…'
      return '仅支持扫码，请使用微信扫一扫'
    },
    isErrHint () {
      return this.expired || !!this.error
    },
    statusHint () {
      if (this.paid) return '支付成功，可关闭本页'
      if (this.expired) return '订单已过期，请重新下单'
      if (this.error) return this.error
      if (this.jsPayMode === 'mp' && !this.error) {
        return this.mpLaunchUrl
          ? '点击下方按钮进入小程序，再点「立即支付」'
          : '小程序打开链接生成失败'
      }
      if (this.showScanShell) {
        return '请使用微信「扫一扫」扫描下方二维码完成支付'
      }
      if (this.inClient && this.jsPayMode !== 'mp') {
        return '正在进入支付…'
      }
      return ''
    }
  },
  mounted () {
    document.title = '微信支付'
    this.ensureStyle()
  },
  beforeDestroy () {
    this.clearTimers()
    this.closeSaveOverlay()
  },
  async created () {
    try {
      const data = await fetchWxJsOrderBrief(this.orderId, this.token)
      if (!data) {
        this.error = '订单加载失败'
        return
      }
      this.applyBrief(data)
      if (data.payError) {
        this.error = data.payError
        return
      }
      if (isPaidStatus(data.status)) {
        this.paid = true
        return
      }
      if (Number(data.status) === STATE_WRONG_AMOUNT) {
        this.error = '金额校验失败，请勿重复支付'
        return
      }
      if (Number(data.status) === STATE_CLOSED) {
        this.error = '订单已关闭'
        return
      }
      if (data.expired) {
        this.expired = true
        this.error = '订单已过期，请重新下单'
        this.startTickTimer()
        return
      }
      this.startTickTimer()
      if (this.jsPayMode === 'mp') {
        this.error = data.mpLaunchError || ''
        return
      }
      if (this.nativeScan) {
        await this.enterScanShell()
        return
      }
      if (!this.inClient) {
        this.showCopyGuide = true
        this.copyBtnText = '复制'
        await this.enterScanShell()
        return
      }
      // OA 调起只在大陆薄壳 oauthCallback / oauthStart，收银页不再 getBrandWCPayRequest
      this.goShellPay()
    } catch (e) {
      this.error = errMsg(e, '加载失败')
    }
  },
  methods: {
    ensureStyle () {
      if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return
      const el = document.createElement('style')
      el.id = STYLE_ID
      // 与 jhuidwx-scan 字号/间距一致（真 px，不被 rem 压缩）
      el.textContent = `
.wxjs-pay-root { min-height: 100vh; background: #f5f5f5; }
.wxjs-pay-root .page { max-width: 420px; margin: 0 auto; padding: 24px 16px 40px; box-sizing: border-box; }
.wxjs-pay-root .brand { font-size: 18px; font-weight: 600; color: #333; text-align: center; }
.wxjs-pay-root .sub { font-size: 13px; color: #999; text-align: center; margin-top: 6px; }
.wxjs-pay-root .card { background: #fff; border-radius: 12px; padding: 24px 20px; margin-top: 20px; box-sizing: border-box; width: 100%; }
.wxjs-pay-root .amount-row { text-align: center; }
.wxjs-pay-root .currency { font-size: 20px; margin-right: 4px; }
.wxjs-pay-root .amount { font-size: 36px; font-weight: 600; }
.wxjs-pay-root .countdown-row { text-align: center; margin-top: 10px; color: #666; font-size: 13px; }
.wxjs-pay-root .countdown-val { color: #1677ff; font-weight: 600; }
.wxjs-pay-root .hint { text-align: center; color: #666; font-size: 13px; line-height: 1.5; margin: 14px 0; }
.wxjs-pay-root .hint.err { color: #f56c6c; }
.wxjs-pay-root .copy-guide { margin: 4px 0 8px; }
.wxjs-pay-root .copy-title {
  margin: 12px 0 6px; text-align: center; color: #333; font-size: 15px; font-weight: 600; line-height: 1.4;
}
.wxjs-pay-root .copy-steps {
  margin: 0; text-align: center; color: #666; font-size: 13px; font-weight: 400; line-height: 1.55;
}
.wxjs-pay-root .copy-guide .qr-link-row { margin-top: 10px; }
.wxjs-pay-root .qr-wrap { text-align: center; margin: 8px 0 4px; }
.wxjs-pay-root .qr-shell { display: inline-block; padding: 10px; background: #fff; border: 1px solid #eee; border-radius: 8px; }
.wxjs-pay-root .qr-guide { margin-top: 10px; color: #333; font-size: 14px; }
.wxjs-pay-root .qr-actions {
  display: flex; gap: 10px; margin: 14px auto 0; max-width: 320px; width: 100%;
  box-sizing: border-box;
}
.wxjs-pay-root .btn-save, .wxjs-pay-root .btn-open-wx {
  flex: 1; min-width: 0; min-height: 44px; padding: 0 8px; border-radius: 8px;
  font-size: 15px; font-weight: 600; cursor: pointer;
}
.wxjs-pay-root .btn-save {
  border: 1px solid #d9d9d9; background: #fff; color: #333;
}
.wxjs-pay-root .btn-save:active { opacity: 0.88; }
.wxjs-pay-root .btn-open-wx {
  border: 0; background: #07c160; color: #fff;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
}
.wxjs-pay-root .btn-open-wx .wx-ico {
  width: 18px; height: 18px; flex: 0 0 auto; display: block;
}
.wxjs-pay-root .btn-open-wx:active { opacity: 0.88; }
.wxjs-pay-root .wx-scan-tip { margin-top: 8px; color: #999; font-size: 12px; line-height: 1.4; }
.wxjs-pay-root .qr-link-row {
  display: flex; flex-wrap: nowrap; align-items: center; gap: 8px;
  margin: 12px 0 0; width: 100%; max-width: 100%;
  text-align: left; font-size: 13px; line-height: 1.4; color: #333;
}
.wxjs-pay-root .qr-link-label { flex: 0 0 auto; color: #333; white-space: nowrap; }
.wxjs-pay-root .qr-link-text {
  flex: 1 1 auto; min-width: 0; color: #1677ff;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  -webkit-user-select: text; user-select: text;
}
.wxjs-pay-root .btn-copy-link {
  flex: 0 0 auto; border: 0; background: #07c160; color: #fff; border-radius: 4px;
  padding: 4px 12px; min-height: 32px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.wxjs-pay-root .btn-copy-link:active { opacity: 0.88; }
.wxjs-pay-root .save-mask {
  position: fixed; inset: 0; z-index: 80; background: rgba(0,0,0,0.72);
  display: flex; align-items: center; justify-content: center; padding: 24px 16px;
  box-sizing: border-box;
}
.wxjs-pay-root .save-panel { width: 100%; max-width: 320px; text-align: center; }
.wxjs-pay-root .save-img {
  width: 100%; max-width: 280px; background: #fff; border-radius: 8px; display: block; margin: 0 auto;
  -webkit-touch-callout: default; -webkit-user-select: auto; user-select: auto;
  pointer-events: auto;
}
.wxjs-pay-root .save-hint { margin: 14px 0 12px; color: #fff; font-size: 15px; }
.wxjs-pay-root .btn-close-save {
  min-height: 40px; padding: 0 28px; border: 1px solid #fff; border-radius: 20px;
  background: transparent; color: #fff; font-size: 15px; cursor: pointer;
}
.wxjs-pay-root .btn-primary {
  display: block; width: 100%; height: 44px; margin-top: 8px; border: 0; border-radius: 8px;
  background: #07c160; color: #fff; font-size: 16px; font-weight: 600; cursor: pointer;
}
.wxjs-pay-root .btn-primary:active { opacity: 0.88; }
.wxjs-pay-root .btn-primary:disabled { opacity: 0.65; cursor: default; }
.wxjs-pay-root .meta { margin-top: 18px; font-size: 12px; color: #999; }
.wxjs-pay-root .meta-row { display: flex; justify-content: space-between; padding: 4px 0; }
.wxjs-pay-root .meta-val { color: #666; max-width: 60%; word-break: break-all; text-align: right; }
`
      document.head.appendChild(el)
    },
    applyBrief (data) {
      this.amountFen = data.amount || 0
      this.amountYuan = data.amountYuan || ''
      this.mchOrderNo = data.mchOrderNo || ''
      this.createTime = data.createTime || ''
      this.oauthStartUrl = data.oauthStartUrl || ''
      this.oauthAuthorizeUrl = data.oauthAuthorizeUrl || ''
      this.jsPayMode = data.jsPayMode === 'mp' ? 'mp' : 'oa'
      this.nativeScan = !!data.nativeScan
      this.showQrLink = !!data.showQrLink
      this.mpLaunchUrl = data.mpLaunchUrl || ''
      this.cashierUrl = data.cashierUrl || ''
      this.expireAtMillis = data.expireAtMillis != null ? Number(data.expireAtMillis) : null
      if (data.expired) {
        this.expired = true
      }
    },
    resolveScanPayUrl () {
      return this.oauthAuthorizeUrl
        || this.oauthStartUrl
        || this.cashierUrl
    },
    resolveOAuthStartUrl () {
      return this.resolveScanPayUrl()
    },
    clearTimers () {
      this.stopQueryPoll()
      if (this.tickTimer) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
      }
      if (this.copyResetTimer) {
        clearTimeout(this.copyResetTimer)
        this.copyResetTimer = null
      }
    },
    startTickTimer () {
      if (this.tickTimer) return
      this.tickTimer = setInterval(() => {
        this.nowMs = Date.now()
        if (this.expireAtMillis && this.nowMs >= this.expireAtMillis && !this.paid) {
          this.expired = true
          this.error = '订单已过期，请重新下单'
          this.showScanShell = false
          this.showCopyGuide = false
          this.closeSaveOverlay()
          this.stopQueryPoll()
          if (this.tickTimer) {
            clearInterval(this.tickTimer)
            this.tickTimer = null
          }
        }
      }, 1000)
    },
    async enterScanShell () {
      this.showScanShell = true
      this.startQueryPoll()
      await this.$nextTick()
      await this.renderScanQr()
    },
    async renderScanQr () {
      const url = this.resolveOAuthStartUrl()
      if (!url || !this.$refs.qrHost || this.expired || this.paid) return
      if (url === this.lastRenderedQr && this.$refs.qrHost.querySelector('img')) return
      // 按页面栏宽度算边长（与空中云汇观感一致），勿用整窗 vw 也勿被 rem 缩小
      const page = this.$el && this.$el.querySelector('.page')
      const w = (page && page.clientWidth) || 390
      const edge = Math.max(260, Math.min(300, Math.floor(w * 0.72)))
      const ok = await renderQrInto(this.$refs.qrHost, url, { pixelSize: edge, errorCorrectionLevel: 'M' })
      if (ok) {
        this.lastRenderedQr = url
      } else {
        this.error = '二维码生成失败，请刷新页面重试'
      }
    },
    openMiniProgram () {
      if (!this.mpLaunchUrl || this.paid || this.expired) return
      window.location.href = this.mpLaunchUrl
    },
    onSaveQr () {
      const src = captureQrDataUrl(this.$refs.qrHost)
      if (!src) {
        this.error = '二维码尚未生成，请稍后重试'
        return
      }
      this.error = ''
      saveQrFromDataUrl(src).then((result) => {
        if (!result || result.mode === 'share' || result.mode === 'cancel') {
          return
        }
        this.revokeSaveObjectUrl()
        this.saveHint = saveHintText()
        this.saveOverlaySrc = result.src
        if (result.revoke) {
          this.saveObjectUrl = result.src
        }
      })
    },
    revokeSaveObjectUrl () {
      if (this.saveObjectUrl) {
        URL.revokeObjectURL(this.saveObjectUrl)
        this.saveObjectUrl = ''
      }
    },
    closeSaveOverlay () {
      this.saveOverlaySrc = ''
      this.revokeSaveObjectUrl()
    },
    onOpenWeixin () {
      openWeixinApp()
    },
    onCopyPayLink () {
      this.copyPayUrl(this.resolveScanPayUrl(), '复制', this.$refs.payLinkText)
    },
    onCopyQrLink () {
      this.copyPayUrl(this.resolveScanPayUrl(), '复制', this.$refs.qrLinkText)
    },
    copyPayUrl (url, idleLabel, linkEl) {
      const idle = idleLabel || '复制'
      if (!url) {
        this.copyBtnText = '无链接'
        this.scheduleCopyReset(idle)
        return
      }
      copyTextReliable(url).then(() => {
        if (isIosLike()) {
          selectElementText(linkEl)
        }
        this.copyBtnText = '已复制'
        this.scheduleCopyReset(idle)
      }).catch(() => {
        selectElementText(linkEl)
        this.copyBtnText = '请长按链接'
        this.scheduleCopyReset(idle)
      })
    },
    scheduleCopyReset (idleLabel) {
      if (this.copyResetTimer) {
        clearTimeout(this.copyResetTimer)
      }
      this.copyResetTimer = setTimeout(() => {
        this.copyBtnText = idleLabel || '复制'
        this.copyResetTimer = null
      }, 1600)
    },
    goShellPay () {
      // 必须走大陆薄壳 oauthStart，禁止回落到境外网关 /api
      const url = this.oauthStartUrl
      if (!url) {
        this.error = '支付入口未配置'
        return
      }
      location.replace(url)
    },
    startQueryPoll () {
      this.stopQueryPoll()
      this.queryTimes = 0
      const tick = async () => {
        this.queryTimes += 1
        if (this.queryTimes > QUERY_MAX_TIMES) {
          this.stopQueryPoll()
          return
        }
        try {
          const data = await queryWxJsOrder(this.orderId, this.token)
          if (data && isPaidStatus(data.status)) {
            this.paid = true
            this.showScanShell = false
            this.showCopyGuide = false
            this.error = ''
            this.closeSaveOverlay()
            this.stopQueryPoll()
            if (this.tickTimer) {
              clearInterval(this.tickTimer)
              this.tickTimer = null
            }
          } else if (data && data.amountMismatch) {
            this.error = data.queryError || '金额校验失败'
            this.stopQueryPoll()
            if (this.tickTimer) {
              clearInterval(this.tickTimer)
              this.tickTimer = null
            }
          } else if (data && (data.closed || Number(data.status) === STATE_CLOSED)) {
            this.error = data.queryError || '订单已关闭'
            this.stopQueryPoll()
            if (this.tickTimer) {
              clearInterval(this.tickTimer)
              this.tickTimer = null
            }
          }
        } catch (e) {
          // ignore
        }
      }
      tick()
      this.queryTimer = setInterval(tick, QUERY_INTERVAL_MS)
    },
    stopQueryPoll () {
      if (this.queryTimer) {
        clearInterval(this.queryTimer)
        this.queryTimer = null
      }
    }
  }
}
</script>
